import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RotateCcw,
  ExternalLink,
  Award,
  Loader2,
  Weight,
  Gauge,
  ArrowUpRight,
  CircleDot,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { addUtmParams } from "@/utils/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const specIcons = {
  flex: <Gauge className="h-4 w-4" />,
  weight: <Weight className="h-4 w-4" />,
  torque: <CircleDot className="h-4 w-4" />,
  kick_point: <ArrowUpRight className="h-4 w-4" />,
  material: <CircleDot className="h-4 w-4" />,
};

export default function SharedResultPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API}/session/${sessionId}`);
        setSession(res.data);
      } catch (e) {
        setError("Results not found or link has expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#03170D] flex items-center justify-center">
        <div className="text-center">
          <div className="shaft-spinner mx-auto mb-4" />
          <p className="text-[#A3B8AA]" style={{ fontFamily: "Outfit, sans-serif" }}>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-[#03170D] flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 text-center max-w-md">
          <h2 className="text-2xl text-white mb-4">Results Not Found</h2>
          <p className="text-[#A3B8AA] mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>{error}</p>
          <Button
            data-testid="find-shaft-btn"
            onClick={() => navigate("/questionnaire")}
            className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full px-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Find Your Shaft
          </Button>
        </div>
      </div>
    );
  }

  const { recommendations = [], general_advice = "", answers = {} } = session;

  return (
    <div className="min-h-screen bg-[#03170D] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <p
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#D4AF37] mb-3"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Shared Recommendations
          </p>
          <h1 data-testid="shared-results-title" className="text-4xl sm:text-5xl tracking-tight leading-none font-medium text-white mb-4">
            Shaft Recommendations
          </h1>
          {general_advice && (
            <p className="text-base text-[#A3B8AA] max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
              {general_advice}
            </p>
          )}
          {answers.club_type && (
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {answers.club_type}
              </Badge>
              <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {answers.swing_speed}
              </Badge>
              <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {answers.golf_journey}
              </Badge>
            </div>
          )}
        </motion.div>

        <div className="flex justify-center mb-10">
          <Button
            data-testid="get-my-recommendations-btn"
            onClick={() => navigate("/questionnaire")}
            className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full px-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Get My Own Recommendations
          </Button>
        </div>

        <div className="space-y-6">
          {recommendations.map((rec, idx) => {
            const isBest = idx === 0;
            return (
              <motion.div
                key={rec.rank || idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`shared-rec-card-${idx}`}
                className={`rounded-2xl p-6 sm:p-8 ${isBest ? "gold-glow bg-[#0A2716]" : "glass-card"}`}
              >
                <div className="flex flex-col sm:flex-row gap-6">
                  {rec.image && (
                    <div className="flex-shrink-0">
                      <img
                        src={rec.image}
                        alt={rec.product_name}
                        className="w-full sm:w-32 h-32 object-contain rounded-xl bg-white/5 p-2"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-start gap-2 mb-3">
                      {isBest && (
                        <Badge className="bg-[#D4AF37] text-[#03170D] font-semibold hover:bg-[#D4AF37]">
                          <Award className="mr-1 h-3 w-3" /> Best Match
                        </Badge>
                      )}
                      <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        #{rec.rank || idx + 1}
                      </Badge>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-medium text-white mb-1">{rec.product_name}</h3>
                    <p className="text-sm text-[#647D6C] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{rec.brand}</p>
                    <p className="text-lg font-semibold text-[#D4AF37] mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>{rec.price}</p>
                    {rec.specs && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {Object.entries(rec.specs).map(([key, val]) =>
                          val ? (
                            <span
                              key={key}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[rgba(26,64,38,0.6)] text-sm text-[#A3B8AA] border border-[#1A4026]"
                              style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                              {specIcons[key] || null}
                              <span className="capitalize">{key.replace("_", " ")}:</span>
                              <span className="text-white">{val}</span>
                            </span>
                          ) : null
                        )}
                      </div>
                    )}
                    {rec.explanation && (
                      <p className="text-base text-[#A3B8AA] leading-relaxed mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {rec.explanation}
                      </p>
                    )}
                    {rec.review_snippet && (
                      <div className="bg-[rgba(10,39,22,0.4)] rounded-lg p-3 mb-4 border-l-2 border-[#D4AF37]">
                        <p className="text-sm italic text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                          &ldquo;{rec.review_snippet}&rdquo;
                        </p>
                      </div>
                    )}
                    {rec.url && (
                      <a href={addUtmParams(rec.url, rec.product_name)} target="_blank" rel="noopener noreferrer">
                        <Button
                          data-testid={`shared-shop-btn-${idx}`}
                          className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full transition-all hover:-translate-y-0.5"
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          Shop Now <ExternalLink className="ml-2 h-4 w-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
